// src/components/SocialMediaSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import './SocialMediaSettings.css';
import { ERROR_MESSAGES, ERROR_CODES } from '../constants/errors'; // Import
import ErrorDisplayModal from './ErrorDisplayModal';
import './ErrorDisplayModal.module.css';

const SocialMediaSettings = () => {
  const {
    // YouTube functions are now included
    connectGoogle,
    connectMeta,
    connectYouTube,
    connectMicrosoft365,
    initTelegramAuth,
    completeTelegramAuth,
    completeTelegram2FAAuth,
    disconnectGoogle,
    disconnectMicrosoft365,
    disconnectMeta,
    disconnectTelegram,
    disconnectYouTube,
    socialMediaConnections,
    fetchSocialMediaConnections,
    error,
    setError,
    clearError, // Global error handling
    setReconnectNetwork,
    reconnectNetwork,
    metaTokenExpired,
    setMetaTokenExpired, // Get Meta status
  } = useData();
  const { currentUser, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // ---- TELEGRAM-specific states ----
  const [tgPhone, setTgPhone] = useState('');
  const [tgCode, setTgCode] = useState('');
  const [tgPassword, setTgPassword] = useState('');
  const [tgStatus, setTgStatus] = useState('');
  const [tgMessage, setTgMessage] = useState('');
  const [tgIsConnected, setTgIsConnected] = useState(false);

  // Define the apps for each column
  const messagingApps = [
    'gmail_1',
    'gmail_2',
    'microsoft365',
    'whatsapp',
    'telegram',
  ];
  const publishingApps = ['youtube', 'instagram', 'facebook'];

  useEffect(() => {
    // Ensure all layers exist before accessing properties
    if (location?.state?.from) {
      sessionStorage.setItem('previousUrl', location.state.from.pathname);
    } else {
      // Fallback to browser history or root path
      sessionStorage.setItem('previousUrl', window.history.state?.url || '/');
    }
  }, [location]);

  useEffect(() => {
    // This ensures that whenever the settings page is loaded, it gets the
    // absolute latest connection statuses from the database, preventing the
    // UI from showing stale "Connected" states after a token revocation.
    if (currentUser) {
      fetchSocialMediaConnections(currentUser.uid);
    }
  }, [currentUser, fetchSocialMediaConnections]);

  // --- useEffect for populating inputs ---
  useEffect(() => {
    if (currentUser && socialMediaConnections) {
      const initialInputs = {};
      const allNetworks = [...messagingApps, ...publishingApps];

      allNetworks.forEach((network) => {
        initialInputs[network] = { default: '' };

        // Handle Gmail accounts (split into gmail_1 and gmail_2)
        if (network.startsWith('gmail_')) {
          const baseNetwork = 'gmail';
          const suffix = network.split('_')[1];
          const index = parseInt(suffix, 10) - 1;
          const userConnections =
            socialMediaConnections[currentUser.uid]?.[baseNetwork];
          if (userConnections) {
            const accountIdentifiers = Object.keys(userConnections);
            if (index < accountIdentifiers.length) {
              initialInputs[network].default = accountIdentifiers[index];
            }
          }
        } else {
          // Handle other networks (Facebook, Instagram, etc.) as before
          const userConnections =
            socialMediaConnections[currentUser.uid]?.[network];
          if (userConnections) {
            const accountIdentifier = Object.keys(userConnections)[0];
            if (accountIdentifier) {
              initialInputs[network].default = accountIdentifier;
            }
          }
        }
      });
      setInputs(initialInputs);
    }
  }, [currentUser, socialMediaConnections]);

  const handleInputChange = (network, accountIdentifier, value) => {
    setInputs((prevInputs) => ({
      ...prevInputs,
      [network]: {
        ...prevInputs[network],
        [accountIdentifier]: value,
      },
    }));
  };

  const getAccountIdentifier = (network, input) => {
    switch (network) {
      case 'gmail_1':
      case 'gmail_2':
      case 'microsoft365':
        return input; // Use email as identifier
      case 'youtube':
      case 'facebook':
      case 'instagram':
        return input; // Use username/channel name as identifier
      case 'whatsapp':
      case 'telegram':
        return input; // Use phone number as identifier
      default:
        return null;
    }
  };

  // ---------------------------
  //        TELEGRAM FLOW
  // ---------------------------

  // 1) Start Auth -> phone => ephemeralSession
  const handleTelegramInitAuth = useCallback(async () => {
    try {
      setTgMessage('');
      setErrorMessage('');
      clearError();
      setLoading(true);

      if (!currentUser?.uid) {
        setErrorMessage(
          'No current user found to associate with Telegram auth.',
        );
        return;
      }

      // Call your front-end function that does the axios call
      const result = await initTelegramAuth({
        userId: currentUser.uid,
        phoneNumber: tgPhone,
      });

      setLoading(false);

      if (!result.status) {
        setErrorMessage(result.message || 'Failed to start Telegram auth');
        return;
      }
      if (result.data.status === 'CODE_REQUIRED') {
        setTgStatus('CODE_REQUIRED');
        setTgMessage('Please enter the code you received via Telegram');
      } else {
        setErrorMessage('Unexpected response from initTelegramAuth');
      }
    } catch (err) {
      console.error('[handleTelegramInitAuth] Error:', err);
      setLoading(false);
      setErrorMessage(err.message || 'Failed to initiate Telegram auth');
    }
  }, [currentUser, tgPhone, clearError, initTelegramAuth]);

  // 2) Complete Auth -> ephemeralSession + code
  const handleTelegramCompleteAuth = useCallback(async () => {
    try {
      setTgMessage('');
      setErrorMessage('');
      clearError();
      setLoading(true);

      if (!currentUser?.uid) {
        setErrorMessage('No current user found to complete Telegram auth.');
        return;
      }

      const result = await completeTelegramAuth({
        userId: currentUser.uid,
        code: tgCode,
      });

      setLoading(false);
      // If not success => might have {status: "PASSWORD_REQUIRED"} or {error message: "..."}
      if (!result.status) {
        if (result.error === 'TELEGRAM_PASSWORD_REQUIRED') {
          setTgStatus('PASSWORD_REQUIRED');
          setTgMessage('Please enter your 2FA password');
        } else {
          setErrorMessage(result.message || 'Failed to complete Telegram auth');
          return;
        }
      } else if (result.data.status === 'success') {
        setTgStatus('AUTH_SUCCESS');
        setTgMessage('You are successfully authenticated on Telegram!');
        setTgIsConnected(true);
        // Call fetchSocialMediaConnections to update the global state
        await fetchSocialMediaConnections(currentUser.uid);
      } else {
        setErrorMessage('Unexpected response from completeTelegramAuth');
      }
    } catch (err) {
      console.error('[handleTelegramCompleteAuth] Error:', err);
      setLoading(false);
      setErrorMessage(err.message || 'Failed to complete Telegram auth');
    }
  }, [
    currentUser,
    tgCode,
    clearError,
    fetchSocialMediaConnections,
    completeTelegramAuth,
  ]);

  // 3) Complete 2FA Auth -> if needed password
  const handleTelegram2FAAuth = useCallback(async () => {
    try {
      setTgMessage('');
      setErrorMessage('');
      clearError();
      setLoading(true);

      if (!currentUser?.uid) {
        setErrorMessage('No current user found to complete Telegram auth.');
        return;
      }

      const result = await completeTelegram2FAAuth({
        userId: currentUser.uid,
        password: tgPassword,
      });

      setLoading(false);

      // If not success => might have {status: "PASSWORD_REQUIRED"} or {error message: "..."}
      if (!result.status) {
        setErrorMessage(result.message || 'Failed to complete Telegram auth');
        return;
      } else {
        setTgStatus('AUTH_SUCCESS');
        setTgMessage('You are successfully authenticated on Telegram!');
        setTgIsConnected(true);
        await fetchSocialMediaConnections(currentUser.uid);
      }
    } catch (err) {
      console.error('[handleTelegramCompleteAuth] Error:', err);
      setLoading(false);
      setErrorMessage(err.message || 'Failed to complete Telegram auth');
    }
  }, [
    currentUser,
    tgPassword,
    clearError,
    fetchSocialMediaConnections,
    completeTelegram2FAAuth,
  ]);
  // If already connected, we won't show phone/code/password.
  // We'll treat "connected" if the DB says there's a valid session for Telegram
  // or if we've gotten "AUTH_SUCCESS" from the step above.

  // This function is called when the user hits the "Connect" button for Telegram
  const handleTelegramConnect = useCallback(async () => {
    setErrorMessage('');
    clearError();

    // If user is not connected (i.e. no ephemeralSession and status=""):
    if (!tgIsConnected && tgStatus === '') {
      if (!tgPhone) {
        setErrorMessage('Please enter a phone number to start Telegram auth.');
        return;
      }
      await handleTelegramInitAuth();
    }
    // If status="CODE_REQUIRED", user must have typed in code
    else if (!tgIsConnected && tgStatus === 'CODE_REQUIRED') {
      if (!tgCode) {
        setErrorMessage('Please enter the code from Telegram.');
        return;
      }
      await handleTelegramCompleteAuth();
    } else if (!tgIsConnected && tgStatus === 'PASSWORD_REQUIRED') {
      if (!tgPassword) {
        setErrorMessage('Please enter your 2FA password.');
        return;
      }
      await handleTelegram2FAAuth();
    }
  }, [
    tgIsConnected,
    tgStatus,
    tgPhone,
    tgCode,
    tgPassword,
    handleTelegramInitAuth,
    handleTelegramCompleteAuth,
    handleTelegram2FAAuth,
    clearError,
  ]);

  const handleTelegramDisconnect = useCallback(async () => {
    clearError();
    setErrorMessage('');
    if (!currentUser?.uid) {
      setErrorMessage('No current user found to disconnect Telegram.');
      return;
    }
    try {
      setLoading(true);
      const input = inputs['telegram']?.default || '';
      const accountIdentifier =
        tgPhone || getAccountIdentifier('telegram', input);
      if (!accountIdentifier) {
        setErrorMessage(
          'Cannot disconnect Telegram without a phone identifier.',
        );
        setLoading(false);
        return;
      }
      const response = await disconnectTelegram({ accountIdentifier });
      if (response.status) {
        setTgIsConnected(false);
        setTgStatus('');
        setTgCode('');
        setTgPassword('');
        setTgMessage('');
        // Call fetchSocialMediaConnections to update the global state
        await fetchSocialMediaConnections(currentUser.uid);
      } else {
        setErrorMessage(response.message || 'Failed to disconnect Telegram');
      }
    } catch (err) {
      console.error('[handleTelegramDisconnect] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [
    clearError,
    currentUser,
    disconnectTelegram,
    tgPhone,
    inputs,
    fetchSocialMediaConnections,
  ]);

  // ---------------------------
  //      GENERIC OAUTH FLOW
  // ---------------------------
  const initiateConnection = useCallback(
    async (network) => {
      setLoading(true);
      clearError();
      setErrorMessage('');
      const input = inputs[network]?.default || '';
      let accountIdentifier = getAccountIdentifier(network, input);
      console.log('Network: ', network);
      const isGoogle = network.startsWith('gmail') || network === 'youtube';
      console.log('isGoogle: ', isGoogle);

      // Allow Google to proceed without input for "Discovery"
      if (!accountIdentifier && !isGoogle) {
        setError(
          ERROR_MESSAGES[ERROR_CODES.INVALID_INPUT] ||
            'Invalid input provided.',
        );
        setLoading(false);
        return;
      }

      // If box is empty, we send 'pending' so the backend knows to discover the ID
      if (!accountIdentifier) accountIdentifier = 'pending';

      let authUrl;
      try {
        switch (network) {
          case 'gmail_1':
          case 'gmail_2':
            authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GMAIL_CLIENT_ID}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_GMAIL_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(process.env.REACT_APP_GMAIL_SCOPES)}&state=${currentUser.uid}:gmail:${accountIdentifier}&access_type=offline&prompt=consent&include_granted_scopes=true`;
            break;
          case 'microsoft365':
            authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_MICROSOFT_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(process.env.REACT_APP_MICROSOFT_REDIRECT_URI)}&response_mode=query&scope=${encodeURIComponent(process.env.REACT_APP_MICROSOFT_SCOPES)}&state=${currentUser.uid}:${network}:${accountIdentifier}&prompt=consent`;
            break;
          case 'youtube':
            authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_YOUTUBE_CLIENT_ID}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_YOUTUBE_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(process.env.REACT_APP_YOUTUBE_SCOPES)}&state=${currentUser.uid}:youtube:${accountIdentifier}&access_type=offline&prompt=consent&include_granted_scopes=true`;
            break;
          case 'facebook':
            authUrl = `https://www.facebook.com/v14.0/dialog/oauth?client_id=${process.env.REACT_APP_FB_CLIENT_ID}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_FB_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent('public_profile,email')}&state=${currentUser.uid}:${network}:${accountIdentifier}`;
            break;
          case 'instagram':
            authUrl = `https://www.facebook.com/v14.0/dialog/oauth?client_id=${process.env.REACT_APP_INSTA_CLIENT_ID}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_INSTA_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent('user_profile,user_media')}&state=${currentUser.uid}:${network}:${accountIdentifier}`;
            break;
          case 'whatsapp':
            authUrl = `https://www.facebook.com/v14.0/dialog/oauth?client_id=${process.env.REACT_APP_WAPP_CLIENT_ID}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_WAPP_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent('whatsapp_business_messaging')}&state=${currentUser.uid}:${network}:${accountIdentifier}`;
            break;
          case 'telegram':
            break; // Handled separately
          default:
            setError(
              ERROR_MESSAGES[ERROR_CODES.INVALID_INPUT] ||
                'Invalid social media network.',
            );
            setLoading(false);
            return;
        }

        if (network !== 'telegram') {
          window.location.href = authUrl;
        }
      } catch (error) {
        console.error(`Error initiating ${network} connection:`, error);
        setError(error.message || `Failed to initiate ${network} connection.`);
      } finally {
        if (network !== 'telegram') {
          setLoading(false);
        }
      }
    },
    [currentUser, inputs, clearError, setError],
  );

  const handleConnect = useCallback(
    async (network) => {
      clearError();
      setErrorMessage('');

      if (network === 'telegram') {
        await handleTelegramConnect();
        return;
      }

      const input = inputs[network]?.default || '';
      let accountIdentifier = getAccountIdentifier(network, input);
      console.log('Network: ', network);
      const isGoogle = network.startsWith('gmail') || network === 'youtube';
      console.log('isGoogle: ', isGoogle);

      // Allow Google to proceed without input for "Discovery"
      if (!accountIdentifier && !isGoogle) {
        setError(
          ERROR_MESSAGES[ERROR_CODES.INVALID_INPUT] ||
            'Invalid input provided.',
        );
        return;
      }

      await initiateConnection(network);
    },
    [inputs, initiateConnection, handleTelegramConnect, clearError, setError],
  );

  const handleDisconnect = useCallback(
    async (network) => {
      clearError();
      setErrorMessage('');

      if (network === 'telegram') {
        await handleTelegramDisconnect();
        return;
      }

      const input = inputs[network]?.default || '';
      const accountIdentifier = getAccountIdentifier(network, input);
      if (!accountIdentifier) {
        setError(
          ERROR_MESSAGES[ERROR_CODES.INVALID_INPUT] ||
            `Please provide a valid identifier for ${network}.`,
        );
        return;
      }
      try {
        setLoading(true);
        let response;
        switch (network) {
          case 'gmail_1':
          case 'gmail_2':
            response = await disconnectGoogle({ accountId: accountIdentifier });
            break;
          case 'microsoft365':
            response = await disconnectMicrosoft365({
              accountId: accountIdentifier,
            });
            break;
          case 'youtube':
            response = await disconnectYouTube({
              accountId: accountIdentifier,
            });
            break;
          case 'facebook':
          case 'instagram':
          case 'whatsapp':
            response = await disconnectMeta({
              network,
              accountId: accountIdentifier,
            });
            break;
          default:
            setError(
              ERROR_MESSAGES[ERROR_CODES.INVALID_INPUT] ||
                'Invalid social media network.',
            );
            setLoading(false);
            return;
        }

        if (response.status) {
          await fetchSocialMediaConnections(currentUser.uid);
        } else {
          setErrorMessage(
            response.message || 'Disconnection failed. Please try again.',
          );
        }
      } catch (error) {
        console.error(`Error disconnecting ${network}:`, error);
        setError(error.message || `Failed to disconnect ${network}.`);
      } finally {
        setLoading(false);
      }
    },
    [
      disconnectGoogle,
      disconnectMicrosoft365,
      disconnectYouTube,
      disconnectMeta,
      handleTelegramDisconnect,
      currentUser,
      inputs,
      fetchSocialMediaConnections,
      clearError,
      setError,
    ],
  );

  useEffect(() => {
    if (isConnecting) return;

    if (authLoading) {
      console.log(
        '[SocialMediaSettings] OAuth callback detected, but waiting for auth to complete...',
      );
      return;
    }

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (code && state) {
      const [userId, network, accountIdentifier] = state.split(':');

      if (currentUser && userId === currentUser.uid) {
        setIsConnecting(true);
        const completeConnection = async () => {
          setLoading(true);
          clearError();
          try {
            let response;
            if (network === 'gmail') {
              response = await connectGoogle({ code, state });
            } else if (network === 'microsoft365') {
              response = await connectMicrosoft365({ code, state });
            } else if (network === 'youtube') {
              response = await connectYouTube({ code, state });
            } else if (
              ['facebook', 'instagram', 'whatsapp'].includes(network)
            ) {
              response = await connectMeta({
                network,
                accountId: accountIdentifier,
                code,
              });
            }

            if (response && response.status) {
              await fetchSocialMediaConnections(currentUser.uid);
              if (['facebook', 'instagram'].includes(network)) {
                setMetaTokenExpired(false);
              }
            } else {
              setErrorMessage(
                response?.message || 'Connection failed. Please try again.',
              );
            }
          } catch (error) {
            console.error(`Error connecting ${network}:`, error);
            setError(error.message || `Failed to connect ${network}.`);
          } finally {
            setLoading(false);
            setIsConnecting(false);
            navigate('/social-media', { replace: true });
          }
        };
        completeConnection();
      }
    }
  }, [
    location,
    currentUser,
    connectGoogle,
    connectMicrosoft365,
    connectYouTube,
    connectMeta,
    navigate,
    fetchSocialMediaConnections,
    isConnecting,
    authLoading,
    clearError,
    setError,
    setMetaTokenExpired,
  ]);

  useEffect(() => {
    if (reconnectNetwork) {
      console.log(
        `SocialMediaSettings: Need to reconnect ${reconnectNetwork}!`,
      );
      clearError();
      setReconnectNetwork(null);
    }
  }, [reconnectNetwork, clearError, setReconnectNetwork]);

  const isTelegramConnected = () => {
    if (tgIsConnected) return true;
    const accountIdentifier = tgPhone || inputs['telegram']?.default || '';
    if (!accountIdentifier) return false;
    const connectionDetails =
      socialMediaConnections[currentUser.uid]?.['telegram']?.[
        accountIdentifier
      ];
    return connectionDetails && !connectionDetails.expired;
  };

  const renderSocialMediaSetting = (network) => {
    const input = inputs[network]?.default || '';
    const accountIdentifier = getAccountIdentifier(network, input);
    let isConnected = false;

    const baseNetwork = network.startsWith('gmail_') ? 'gmail' : network;
    const networkGroup =
      socialMediaConnections[currentUser.uid]?.[baseNetwork] || {};

    // Find the connection for this specific slot or ID
    const connectionDetails = Object.values(networkGroup).find(
      (conn) =>
        conn.accountIdentifier === accountIdentifier ||
        (network.startsWith('gmail_') &&
          Object.keys(networkGroup)[parseInt(network.split('_')[1]) - 1] ===
            conn.accountIdentifier),
    );

    // Prioritize the Name for display if connected
    let displayValue = input;
    if (connectionDetails && !connectionDetails.expired) {
      // For Telegram, we only show accountName or the raw phone number (input)
      // because accountIdentifier is encrypted.
      if (network === 'telegram') {
        displayValue = connectionDetails.accountName || input;
      } else {
        displayValue =
          connectionDetails.accountName ||
          connectionDetails.accountIdentifier ||
          input;
      }
    }

    if (network === 'telegram') {
      isConnected = isTelegramConnected();
    } else if (network === 'facebook' || network === 'instagram') {
      isConnected =
        connectionDetails && !metaTokenExpired && !connectionDetails.expired;
    } else {
      isConnected = connectionDetails && !connectionDetails.expired;
    }

    const isLoading = loading;

    // Special rendering for Telegram
    if (network === 'telegram') {
      return (
        <div key={network} className="social-media-setting">
          <label>Telegram:</label>
          <div className="social-media-input-group">
            {isConnected ? (
              <>
                <input
                  type="text"
                  value={displayValue}
                  readOnly
                  disabled={isLoading}
                  className="connected-input"
                />
                <button
                  onClick={() => handleDisconnect('telegram')}
                  disabled={isLoading}
                  className="connected"
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>Disconnecting...
                    </>
                  ) : (
                    'Connected'
                  )}
                </button>
              </>
            ) : (
              <div className="telegram-connect-flow">
                {tgStatus === '' && (
                  <input
                    type="text"
                    placeholder="Enter phone number"
                    value={tgPhone}
                    onChange={(e) => setTgPhone(e.target.value)}
                    disabled={isLoading}
                  />
                )}
                {tgStatus === 'CODE_REQUIRED' && (
                  <input
                    type="text"
                    placeholder="Enter code from Telegram"
                    value={tgCode}
                    onChange={(e) => setTgCode(e.target.value)}
                    disabled={isLoading}
                  />
                )}
                {tgStatus === 'PASSWORD_REQUIRED' && (
                  <input
                    type="password"
                    placeholder="Enter 2FA password"
                    value={tgPassword}
                    onChange={(e) => setTgPassword(e.target.value)}
                    disabled={isLoading}
                  />
                )}
                <button
                  onClick={() => handleConnect('telegram')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner"></span>Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
                {tgMessage && (
                  <div className="telegram-message">{tgMessage}</div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    let displayLabel =
      network.charAt(0).toUpperCase() + network.replace('_', ' ').slice(1);
    if (network === 'microsoft365') displayLabel = 'Microsoft 365'; // <-- Formatting tweak
    // Standard rendering for all other networks
    return (
      <div key={network} className="social-media-setting">
        <label>{displayLabel}:</label>
        <div className="social-media-input-group">
          <input
            type="text"
            placeholder={
              network.includes('gmail') || network === 'microsoft365'
                ? 'Enter email'
                : ['facebook', 'instagram', 'youtube'].includes(network)
                  ? 'Enter username or ID'
                  : 'Enter phone number'
            }
            value={displayValue}
            onChange={(e) =>
              handleInputChange(network, 'default', e.target.value)
            }
            disabled={isLoading || isConnected}
          />
          <button
            onClick={() =>
              isConnected ? handleDisconnect(network) : handleConnect(network)
            }
            className={isConnected ? 'connected' : ''}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>Working...
              </>
            ) : isConnected ? (
              'Connected'
            ) : (
              'Connect'
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="social-media-settings-container">
      <div className="modal-header">
        {' '}
        {/* <-- ADD THIS WRAPPER */}
        <h2>Social Media Settings</h2>
        <button
          onClick={() => {
            const previousUrl = sessionStorage.getItem('previousUrl') || '/';
            navigate(previousUrl);
            sessionStorage.removeItem('previousUrl');
          }}
          className="closeTheButton"
        >
          &times;
        </button>
      </div>{' '}
      {/* <-- AND CLOSE IT HERE */}
      {error && <ErrorDisplayModal error={error} clearError={clearError} />}
      {errorMessage && <span className="error-message">{errorMessage}</span>}
      <div className="settings-layout">
        <div className="settings-column">
          <h3 className="column-title">Messaging</h3>
          {messagingApps.map((network) => renderSocialMediaSetting(network))}
        </div>
        <div className="settings-column">
          <h3 className="column-title">Publishing Channels</h3>
          {publishingApps.map((network) => renderSocialMediaSetting(network))}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaSettings;
